<template>
  <div class="comparison">
    <p>
      Fairfield City Council has one of the lowest residential rates and charges of neighbouring councils.
    </p>
    <h3>
      Comparison of Residential Rates and Charges for neighbouring councils
    </h3>
    <canvas id="compChart"></canvas>

    <p>
      The graph above shows comparable residential rates and charges for
      neighbouring councils using the same land value you entered and a
      standard 240 litre bin for domestic waste.  The discount for pensioner
      rebates ($250) is not included in these amounts.
    </p>

    <p>
      For comparative purposes the prior year’s rates and charges in the graph above are calculated
      using the same land value as the current year. Land values are based on
      land valuations as at 1 July 2024. The land valuations are provided to councils by the NSW Valuer
      General every 3 years.
    </p>
  </div>
</template>

<script>
import Chart from 'chart.js';
import * as ChartAnnotation from 'chartjs-plugin-annotation';
import calculateRates from "@/calculateRates";

export default {
  name: "Comparison",
  props: {
    landValue: {
      type: Number,
      required: true
    }
  },
  methods: {
    makeChart: async function() {
      let config = {
        type: 'bar',
        data: {
          labels: [],
          datasets: [
            {
              label: '2025/2026',
              data: [],
              backgroundColor: [],
              stack: '1'
            },
            {
              label: '2026/2027',
              data: [],
              backgroundColor: [],
              stack: '0'
            }
          ]
        },
        options: {
          maintainAspectRatio: false,
          animation: {
            duration: 3000
          },
          title: {
            display: false,
            fontFamily: "'Arial', sans-serif",
            text: "Comparison of Residential Rates and Charges for neighbouring councils"
          },
          tooltips: {
            callbacks: {
              label: (item, data) => {
                let label = data.datasets[item.datasetIndex].label || '';
                if (label) {
                  label += ': ';
                }
                label += `${item.yLabel.toLocaleString('en-au', {
                  style: 'currency',
                  currency: 'AUD',
                  currencyDisplay: 'symbol'
                })}`;
                return label;
              }
            },
            mode: 'index',
            intersect: false
          },
          scales: {
            yAxes: [{
              ticks: {
                fontFamily: "'Arial', sans-serif",
                beginAtZero: true,
                callback: function(value) {
                  return '$' + value.toLocaleString();
                }
              },
              stacked: true
            }],
            xAxes: [{
              ticks: {
                fontFamily: "'Arial', sans-serif",
                fontStyle: "bold"
              },
              stacked: true
            }]
          },
          annotation: {
            annotations: [{
              id: 'comparison',
              type: 'line',
              mode: 'horizontal',
              scaleID: 'y-axis-0',
              borderColor: 'rgba(0, 80, 158, 1)',
              borderWidth: 2,
              borderDash: [2, 2]
            }]
          }
        },
        plugins: [ChartAnnotation]
      };
      let councilData = await (await fetch('data/comparison.json')).json();
      councilData.sort((lhs, rhs) => {
        if(lhs['name'] === 'Fairfield') {
          return -1;
        }
        if(rhs['name'] === 'Fairfield') {
          return 1;
        }
        if (lhs['name'] < rhs['name']) {
          return -1;
        }
        return 1;
      });
      councilData.forEach((council) => {
        config.data.labels.push(council['name']);
        let oldYear = true;
        council['data'].forEach((finYear) => {
          let dataset = config.data.datasets.find((el) => {
            return el.label === finYear['year']
          });
          if(!dataset) {
            return;
          }
          let colour = 'rgba(0, 80, 158, 1)';
          if (oldYear) {
            colour = 'rgba(166, 206, 57, 1)';
            oldYear = false;
          }
          dataset.backgroundColor.push(colour);
          let rates = calculateRates(this.landValue, finYear['base'], finYear['advalorem'],
              finYear['minimum'], finYear['rebate']);
          let waste = finYear['waste'];
          let stormwater = finYear['stormwater'];
          let envCharge = this.landValue * finYear['environmentalFactor'];
          let fullRates = parseFloat((rates + waste + stormwater + envCharge).toFixed(2));
          dataset.data.push(fullRates);
          if (council['name'] === 'Fairfield' && !oldYear) {
            config.options.annotation.annotations[0].value = fullRates;
          }
        });
      });
      // eslint-disable-next-line no-unused-vars
      let chart = new Chart('compChart', config);
    }
  },
  mounted() {
    this.makeChart(this);
  }
}
</script>

<style scoped>
div.comparison {
  display: flex;
  flex-direction: column;
  align-items: center;
}

canvas#compChart {
  max-width: 700px;
  max-height: 350px;
  margin-bottom: 1rem;
}

p, ul, h3 {
  max-width: 100%;
}
</style>